import React, { FC, useState } from 'react';
import AsyncSelect from 'react-select/async';
import Select from 'react-select';
import NoSsr from '@common/noSsr';
import {
  ReactSelectInputStyles,
  ReactSelectStyles,
} from '@components/profile/profileOverlay/overlays/ReactSelectStyles';
import useSkillKeyword from '@components/profile/profileOverlay/overlays/skill/query/useSkillKeyword';
import SkillKeywordChipList from '@components/phs/test/keywordModal/SkillKeywordChipList';
import TooltipButton from '@components/phs/test/keywordModal/TooltipButton';
import RecommendSkillContents from '@components/profile/profileOverlay/overlays/skill/skillWrite/skillKeywordForm/RecommendSkillContents';
import { SkillVO } from '@domain/vo/profile/ProfileSkillVO';
import SkillKeywordDto from '@domain/rs/profile/skill/SkillKeywordRs';
import { ProfileSkillDto } from '@domain/rs/phs/center/ProfileSkillDto';
import { SkillLevelOptions, SkillLevelType } from '@domain/constant/profile/SkillType';
import { LoadOptions } from '@domain/constant/ReactSelectOption';
import { V2Route } from '@utils/routeUtil';
import useDebounce from '@utils/hooks/useDebounce';
import classnames from 'classnames/bind';
import styles from './skillKeyword.module.scss';
const cx = classnames.bind(styles);

interface SkillKeywordProps {
  scrollRef: React.RefObject<HTMLDivElement>;
  selectedSkills: ProfileSkillDto[];
  setSelectedSkills: React.Dispatch<React.SetStateAction<ProfileSkillDto[]>>;
}

const SkillKeyword: FC<SkillKeywordProps> = ({ scrollRef, selectedSkills, setSelectedSkills }) => {
  const { mutateAsync: skillKeywordMutateAsync } = useSkillKeyword();

  const [selectedSkill, setSelectedSkill] = useState<SkillKeywordDto | null>(null);
  const [lastSelectedSkill, setLastSelectedSkill] = useState<SkillKeywordDto | null>(null);

  const loadOptions = useDebounce((keyword: string, callback: LoadOptions) => {
    skillKeywordMutateAsync(keyword).then((skillKeywords: SkillKeywordDto[]) => {
      //선택된 항목 option에서 제거
      const selectedList = selectedSkills?.map((selected) => selected.code) || [];
      callback(skillKeywords.filter((item) => !selectedList.includes(item.code)));
    });
  }, 100);

  const onSelectSkill = (selected: any) => {
    setSelectedSkill(selected as SkillKeywordDto);
  };

  const onSelectSkillLevel = (selected: any) => {
    if (!selectedSkill) return;

    setSelectedSkills((prev) => [
      ...prev,
      {
        code: selectedSkill.code,
        keyword: selectedSkill.keyword,
        level: selected.value as SkillLevelType,
      },
    ]);

    setLastSelectedSkill(selectedSkill);
    setSelectedSkill(null);
  };

  const scrollToTop = () => {
    const scrollEl = scrollRef.current;
    if (scrollEl) scrollEl.scrollTop = 0;
  };

  const onSelectRecommendSkill = (selected: SkillKeywordDto) => {
    scrollToTop();
    setSelectedSkill(selected);
  };

  return (
    <div className={cx('keywordArea')}>
      <div className={cx('sectionTitleArea')}>
        <em className={cx('sectionTitle')}>
          기술 키워드
          <TooltipButton />
        </em>

        <a
          href={V2Route.getRequestSkillKeywordPage()}
          className={cx('requestKeyword')}
          target="_blank"
          rel="noreferrer"
        >
          기술 키워드 요청하기
        </a>
      </div>

      <div className={cx('selectArea')}>
        <div className={cx('selectInner')}>
          <NoSsr>
            <AsyncSelect
              className={cx('select')}
              placeholder={'기술 키워드를 검색해주세요.'}
              loadOptions={loadOptions}
              getOptionValue={(option) => option.code}
              getOptionLabel={(option) => option.keyword}
              openMenuOnClick={false}
              styles={ReactSelectInputStyles}
              isClearable={true}
              onChange={onSelectSkill}
              value={selectedSkill}
            />
          </NoSsr>
        </div>

        <div className={cx('selectInner')}>
          <Select
            className={cx('select')}
            placeholder={'숙련도'}
            styles={ReactSelectStyles}
            isSearchable={false}
            options={SkillLevelOptions}
            onChange={onSelectSkillLevel}
            value={null}
            isDisabled={!selectedSkill}
          />
        </div>
      </div>

      {/*/!* 선택한 아이템 영역 *!/*/}
      <SkillKeywordChipList selectedSkills={selectedSkills} setSelectedSkills={setSelectedSkills} />

      {/*/!* 추천 키워드 *!/*/}
      <RecommendSkillContents
        skillKeyword={lastSelectedSkill as SkillVO}
        selectedSkillKeywords={selectedSkills as SkillVO[]}
        onSelectSkill={onSelectRecommendSkill}
      />
    </div>
  );
};

export default SkillKeyword;
